import { useState, useMemo, useEffect } from "react";

const HST_RATES = {
  "ON — 13%": 0.13,
  "NS / NB / PEI / NL — 15%": 0.15,
  "AB — 5%": 0.05,
  "BC — 5%": 0.05,
  "QC — 5%": 0.05,
  "SK — 5%": 0.05,
  "MB — 5%": 0.05,
};

const THRESHOLD = 30000;
const MAX_ENTRIES = 500;
let _incId = 10, _expId = 10;

function fmt(n) {
  return n.toLocaleString("en-CA", { style: "currency", currency: "CAD", minimumFractionDigits: 2 });
}

function getQuarter(d) {
  return Math.floor(new Date(d).getMonth() / 3);
}

const Q_LABELS = ["Q1 Jan–Mar", "Q2 Apr–Jun", "Q3 Jul–Sep", "Q4 Oct–Dec"];

function generatePDF(income, expenses, province, totRevenue, totHST, totITC, netOwing, threshPct, businessName) {
  const now = new Date().toLocaleDateString("en-CA", { year: "numeric", month: "long", day: "numeric" });
  const year = new Date().getFullYear();
  const threshColor = threshPct > 85 ? "#C94A2D" : threshPct > 60 ? "#C9862D" : "#2A7A4F";
  const threshWidth = Math.min(100, threshPct);

  const incRows = income.map(r => `
    <tr>
      <td>${r.date}</td><td>${r.client}</td>
      <td style="text-align:right">${fmt(r.amount)}</td>
      <td style="text-align:right;color:#2A7A4F">${fmt(r.hst)}</td>
    </tr>`).join("") || `<tr><td colspan="4" style="color:#aaa;text-align:center">No entries</td></tr>`;

  const expRows = expenses.map(r => `
    <tr>
      <td>${r.date}</td><td>${r.desc}</td>
      <td style="text-align:right">${fmt(r.amount)}</td>
      <td style="text-align:right;color:#2A7A4F">${fmt(r.itc)}</td>
    </tr>`).join("") || `<tr><td colspan="4" style="color:#aaa;text-align:center">No entries</td></tr>`;

  const html = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title>myhst.ca — HST Summary ${year}</title>
<style>
  body { font-family: Georgia, serif; color: #1A1A1A; max-width: 720px; margin: 40px auto; padding: 0 24px; font-size: 13px; }
  .header { border-bottom: 2px solid #1B3A2D; padding-bottom: 16px; margin-bottom: 24px; display: flex; justify-content: space-between; align-items: flex-end; }
  .logo { font-size: 22px; font-weight: bold; color: #1B3A2D; }
  .meta { font-size: 11px; color: #888; text-align: right; line-height: 1.6; }
  .summary { display: grid; grid-template-columns: repeat(4,1fr); gap: 12px; margin-bottom: 28px; }
  .card { border: 1px solid #E0DDD5; border-radius: 6px; padding: 12px; }
  .card-label { font-size: 10px; color: #888; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px; }
  .card-val { font-size: 17px; font-weight: bold; font-family: monospace; }
  .warn { background: #FFFBEF; border-color: #E0C060; }
  .warn .card-val { color: #8A5A00; }
  h2 { font-size: 13px; text-transform: uppercase; letter-spacing: 1px; color: #2A7A4F; border-bottom: 1px solid #E0DDD5; padding-bottom: 6px; margin: 24px 0 12px; }
  table { width: 100%; border-collapse: collapse; font-size: 12px; }
  th { text-align: left; padding: 6px 8px; font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px; color: #888; border-bottom: 1px solid #E0DDD5; }
  td { padding: 7px 8px; border-bottom: 1px solid #F2EFE8; }
  .total-row td { font-weight: bold; background: #F9F7F2; border-top: 1px solid #D0CCC4; }
  .thresh { margin: 0 0 24px; }
  .thresh-label { font-size: 11px; color: #888; margin-bottom: 4px; display: flex; justify-content: space-between; }
  .thresh-bar { background: #E0DDD5; height: 6px; border-radius: 3px; overflow: hidden; }
  .thresh-fill { height: 100%; border-radius: 3px; background: ${threshColor}; width: ${threshWidth}%; }
  .disclaimer { margin-top: 32px; padding-top: 16px; border-top: 1px solid #E0DDD5; font-size: 10px; color: #AAA; line-height: 1.6; }
</style>
</head>
<body>
<div class="header">
  <div>
    <div class="logo">myhst.ca</div>
    ${businessName ? `<div style="font-size:14px;color:#1B3A2D;margin-top:4px;font-weight:bold;">${businessName}</div>` : ""}
  </div>
  <div class="meta">HST Summary Report<br>${province}<br>Generated ${now}</div>
</div>

<div class="summary">
  <div class="card"><div class="card-label">Total revenue</div><div class="card-val">${fmt(totRevenue)}</div></div>
  <div class="card"><div class="card-label">HST collected</div><div class="card-val">${fmt(totHST)}</div></div>
  <div class="card"><div class="card-label">Input tax credits</div><div class="card-val">${fmt(totITC)}</div></div>
  <div class="card ${netOwing > 0 ? "warn" : ""}"><div class="card-label">Net owing</div><div class="card-val">${fmt(Math.abs(netOwing))}${netOwing < 0 ? " refund" : ""}</div></div>
</div>

<div class="thresh">
  <div class="thresh-label"><span>Registration threshold — ${fmt(totRevenue)} of ${fmt(30000)}</span><span>${Math.round(threshPct)}%</span></div>
  <div class="thresh-bar"><div class="thresh-fill"></div></div>
</div>

<h2>Income</h2>
<table>
  <thead><tr><th>Date</th><th>Client</th><th style="text-align:right">Pre-tax amount</th><th style="text-align:right">HST collected</th></tr></thead>
  <tbody>${incRows}</tbody>
  <tfoot><tr class="total-row"><td colspan="2">Total</td><td style="text-align:right">${fmt(totRevenue)}</td><td style="text-align:right;color:#2A7A4F">${fmt(totHST)}</td></tr></tfoot>
</table>

<h2>Expenses &amp; ITCs</h2>
<table>
  <thead><tr><th>Date</th><th>Description</th><th style="text-align:right">Pre-tax amount</th><th style="text-align:right">ITC claimable</th></tr></thead>
  <tbody>${expRows}</tbody>
  <tfoot><tr class="total-row"><td colspan="2">Total ITCs</td><td></td><td style="text-align:right;color:#2A7A4F">${fmt(totITC)}</td></tr></tfoot>
</table>

<div class="disclaimer">
  myhst.ca is a calculation tool for informational purposes only and does not constitute tax advice. Always verify your remittance amount with the CRA or a qualified tax professional before filing. HST rates current as of ${year}.
</div>
</body>
</html>`;

  const blob = new Blob([html], { type: "text/html" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `myhst-summary-${year}.html`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function MyHST() {
  const [province, setProvince] = useState("ON — 13%");
  const [tab, setTab] = useState("income");
  const [emailSent, setEmailSent] = useState(false);
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [sending, setSending] = useState(false);
  const [showTracker, setShowTracker] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [businessName, setBusinessName] = useState("");
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [downloadEmail, setDownloadEmail] = useState("");
  const [downloadEmailError, setDownloadEmailError] = useState("");

  const [income, setIncome] = useState([]);
  const [expenses, setExpenses] = useState([]);

  const rate = HST_RATES[province];
  const totRevenue = useMemo(() => income.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0), [income]);
  const totHST = useMemo(() => income.reduce((s, r) => s + (parseFloat(r.hst) || 0), 0), [income]);
  const totITC = useMemo(() => expenses.reduce((s, r) => s + (parseFloat(r.itc) || 0), 0), [expenses]);
  const netOwing = totHST - totITC;
  const threshPct = Math.min(100, (totRevenue / THRESHOLD) * 100);

  function addIncomeRow() {
    if (income.length >= MAX_ENTRIES) return;
    const today = new Date().toISOString().split("T")[0];
    setIncome(p => [...p, { id: ++_incId, date: today, client: "", amount: "", hst: "" }]);
  }
  function addExpenseRow() {
    if (expenses.length >= MAX_ENTRIES) return;
    const today = new Date().toISOString().split("T")[0];
    setExpenses(p => [...p, { id: ++_expId, date: today, desc: "", amount: "", itc: "" }]);
  }
  function updateIncome(id, field, value) {
    setIncome(p => p.map(r => {
      if (r.id !== id) return r;
      const updated = { ...r, [field]: value };
      if (field === "amount") {
        const amt = parseFloat(value) || 0;
        updated.hst = amt > 0 ? parseFloat((amt * rate).toFixed(2)) : "";
      }
      return updated;
    }));
  }
  function updateExpense(id, field, value) {
    setExpenses(p => p.map(r => {
      if (r.id !== id) return r;
      const updated = { ...r, [field]: value };
      if (field === "amount") {
        const amt = parseFloat(value) || 0;
        updated.itc = amt > 0 ? parseFloat((amt * rate).toFixed(2)) : "";
      }
      return updated;
    }));
  }

  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = "https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,300;0,9..144,600;1,9..144,300&family=DM+Mono:wght@400;500&family=DM+Sans:wght@400;500&display=swap";
    document.head.appendChild(link);
  }, []);

  async function handleEmail() {
    if (!email || !email.includes("@")) { setEmailError("Please enter a valid email address."); return; }
    setEmailError("");
    setSending(true);
    try {
      await fetch("https://formspree.io/f/meenpord", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, source: "myhst.ca-waitlist" }),
      });
    } catch (_) {}
    setSending(false);
    setEmailSent(true);
  }

  function handleDownloadClick() {
    if (emailSent) {
      generatePDF(income, expenses, province, totRevenue, totHST, totITC, netOwing, threshPct, businessName);
    } else {
      setShowDownloadModal(true);
    }
  }

  async function handleDownloadWithEmail() {
    if (!downloadEmail || !downloadEmail.includes("@")) { setDownloadEmailError("Please enter a valid email address."); return; }
    setDownloadEmailError("");
    try {
      await fetch("https://formspree.io/f/meenpord", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: downloadEmail, source: "myhst.ca-download" }),
      });
    } catch (_) {}
    setEmail(downloadEmail);
    setEmailSent(true);
    setShowDownloadModal(false);
    generatePDF(income, expenses, province, totRevenue, totHST, totITC, netOwing, threshPct, businessName);
  }

  const qData = Q_LABELS.map((label, qi) => {
    const qInc = income.filter(r => getQuarter(r.date) === qi);
    const qExp = expenses.filter(r => getQuarter(r.date) === qi);
    const collected = qInc.reduce((s, r) => s + r.hst, 0);
    const itcs = qExp.reduce((s, r) => s + r.itc, 0);
    return { label, collected, itcs, net: collected - itcs };
  });

  const c = {
    root: { fontFamily: "'DM Sans', system-ui, sans-serif", background: "#F5F2EC", minHeight: "100vh", color: "#1A1A1A" },
    nav: { background: "#1B3A2D", padding: "0 32px", height: "56px", display: "flex", alignItems: "center", justifyContent: "space-between" },
    navLogo: { fontFamily: "'Fraunces', Georgia, serif", fontSize: "22px", fontWeight: "600", color: "#F5F2EC", letterSpacing: "-0.5px" },
    navTag: { fontSize: "11px", color: "#7FB89A", letterSpacing: "1px", textTransform: "uppercase" },
    hero: { background: "#1B3A2D", padding: "72px 32px 80px", textAlign: "center" },
    heroEyebrow: { fontFamily: "'DM Mono', monospace", fontSize: "11px", color: "#7FB89A", letterSpacing: "2px", textTransform: "uppercase", marginBottom: "20px" },
    heroH1: { fontFamily: "'Fraunces', Georgia, serif", fontSize: "clamp(36px, 6vw, 64px)", fontWeight: "300", color: "#F5F2EC", lineHeight: "1.1", letterSpacing: "-1px", margin: "0 0 20px", maxWidth: "720px", marginLeft: "auto", marginRight: "auto" },
    heroH1Em: { fontStyle: "italic", color: "#7FB89A" },
    heroSub: { fontSize: "17px", color: "#A8C4B0", lineHeight: "1.6", maxWidth: "480px", margin: "0 auto 36px" },
    heroCTA: { background: "#2A7A4F", color: "#F5F2EC", border: "none", padding: "14px 32px", fontSize: "15px", fontWeight: "500", borderRadius: "6px", cursor: "pointer", fontFamily: "inherit", letterSpacing: "0.2px" },
    heroStatRow: { display: "flex", justifyContent: "center", gap: "48px", marginTop: "56px", paddingTop: "40px", borderTop: "1px solid rgba(255,255,255,0.08)" },
    heroStat: { textAlign: "center" },
    heroStatNum: { fontFamily: "'DM Mono', monospace", fontSize: "28px", color: "#F5F2EC", fontWeight: "500" },
    heroStatLabel: { fontSize: "12px", color: "#7FB89A", marginTop: "4px" },
    pillars: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px,1fr))", gap: "1px", background: "#D8D4CB", margin: "0" },
    pillar: { background: "#F5F2EC", padding: "40px 32px" },
    pillarNum: { fontFamily: "'DM Mono', monospace", fontSize: "11px", color: "#2A7A4F", letterSpacing: "1px", marginBottom: "16px" },
    pillarTitle: { fontFamily: "'Fraunces', Georgia, serif", fontSize: "22px", fontWeight: "600", marginBottom: "10px", lineHeight: "1.2" },
    pillarBody: { fontSize: "14px", color: "#5A6B62", lineHeight: "1.65" },
    trackerWrap: { background: "#FFFFFF", borderTop: "1px solid #D8D4CB" },
    trackerHead: { background: "#1B3A2D", padding: "32px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "16px" },
    trackerTitle: { fontFamily: "'Fraunces', Georgia, serif", fontSize: "24px", color: "#F5F2EC", fontWeight: "300" },
    provSelect: { background: "#243F30", border: "1px solid #3A5E47", color: "#F5F2EC", padding: "8px 12px", fontSize: "13px", borderRadius: "5px", fontFamily: "inherit" },
    summGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px,1fr))", gap: "1px", background: "#E8E4DC" },
    summCard: (warn) => ({ background: warn ? "#FFFBEF" : "#FFFFFF", padding: "20px 24px" }),
    summLabel: { fontSize: "10px", color: "#8A9890", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: "6px" },
    summVal: (warn) => ({ fontFamily: "'DM Mono', monospace", fontSize: "22px", fontWeight: "500", color: warn ? "#8A5A00" : "#1A1A1A" }),
    summSub: { fontSize: "11px", color: "#AAB4AC", marginTop: "3px" },
    threshWrap: { padding: "16px 24px", background: "#F9F7F2", borderBottom: "1px solid #E8E4DC" },
    threshRow: { display: "flex", justifyContent: "space-between", fontSize: "12px", color: "#8A9890", marginBottom: "6px" },
    threshBar: { background: "#E0DDD5", borderRadius: "3px", height: "5px", overflow: "hidden" },
    threshFill: (pct) => ({ width: pct + "%", height: "100%", borderRadius: "3px", background: pct > 85 ? "#C94A2D" : pct > 60 ? "#C9862D" : "#2A7A4F", transition: "width 0.4s" }),
    tabRow: { display: "flex", borderBottom: "1px solid #E8E4DC", padding: "0 24px" },
    tabBtn: (active) => ({ padding: "12px 16px", fontSize: "13px", fontWeight: active ? "500" : "400", color: active ? "#1A1A1A" : "#8A9890", background: "none", border: "none", borderBottom: active ? "2px solid #2A7A4F" : "2px solid transparent", cursor: "pointer", fontFamily: "inherit", marginBottom: "-1px" }),
    table: { width: "100%", borderCollapse: "collapse", fontSize: "13px" },
    th: { padding: "10px 16px", textAlign: "left", fontSize: "10px", color: "#8A9890", textTransform: "uppercase", letterSpacing: "0.5px", borderBottom: "1px solid #E8E4DC", fontWeight: "500" },
    td: { padding: "11px 16px", borderBottom: "1px solid #F2EFE8", color: "#3A3A3A" },
    tdR: { padding: "11px 16px", borderBottom: "1px solid #F2EFE8", textAlign: "right", fontFamily: "'DM Mono', monospace", color: "#1A1A1A" },
    tdG: { padding: "11px 16px", borderBottom: "1px solid #F2EFE8", textAlign: "right", fontFamily: "'DM Mono', monospace", color: "#2A7A4F" },
    addRow: { display: "flex", gap: "8px", padding: "12px 16px", background: "#F9F7F2", borderTop: "1px solid #E8E4DC", flexWrap: "wrap" },
    inp: { background: "#FFF", border: "1px solid #D8D4CB", borderRadius: "4px", padding: "7px 10px", fontSize: "13px", fontFamily: "inherit", color: "#1A1A1A", outline: "none" },
    addBtn: { background: "#1B3A2D", color: "#F5F2EC", border: "none", borderRadius: "4px", padding: "7px 16px", fontSize: "13px", cursor: "pointer", fontFamily: "inherit", fontWeight: "500" },
    delBtn: { background: "none", border: "none", color: "#C8C4BC", cursor: "pointer", fontSize: "16px", padding: "0 4px" },
    inlineInp: { background: "transparent", border: "none", borderBottom: "1px solid transparent", padding: "2px 4px", fontSize: "13px", fontFamily: "inherit", color: "#1A1A1A", outline: "none", width: "100%", borderRadius: "0", MozAppearance: "textfield", WebkitAppearance: "none" },
    emailSection: { background: "#1B3A2D", padding: "72px 32px", textAlign: "center" },
    emailH2: { fontFamily: "'Fraunces', Georgia, serif", fontSize: "clamp(28px,4vw,42px)", fontWeight: "300", color: "#F5F2EC", marginBottom: "12px", lineHeight: "1.2" },
    emailSub: { fontSize: "15px", color: "#A8C4B0", marginBottom: "32px" },
    emailForm: { display: "flex", gap: "10px", justifyContent: "center", flexWrap: "wrap", maxWidth: "440px", margin: "0 auto" },
    emailInp: { flex: "1", minWidth: "220px", background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: "5px", padding: "12px 16px", fontSize: "14px", color: "#F5F2EC", fontFamily: "inherit", outline: "none" },
    emailBtn: { background: "#2A7A4F", color: "#F5F2EC", border: "none", borderRadius: "5px", padding: "12px 24px", fontSize: "14px", fontWeight: "500", cursor: "pointer", fontFamily: "inherit" },
    emailErr: { color: "#F4A26A", fontSize: "12px", marginTop: "8px" },
    successMsg: { fontFamily: "'Fraunces', Georgia, serif", fontSize: "22px", fontStyle: "italic", color: "#7FB89A" },
    footer: { background: "#131F18", padding: "24px 32px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px" },
    footerLogo: { fontFamily: "'Fraunces', Georgia, serif", fontSize: "16px", color: "#7FB89A" },
    footerNote: { fontSize: "11px", color: "#4A6655" },
    footerLink: { fontSize: "11px", color: "#4A6655", background: "none", border: "none", cursor: "pointer", textDecoration: "underline", fontFamily: "inherit", padding: 0 },
    disclaimer: { background: "#F9F7F2", borderTop: "1px solid #E8E4DC", borderBottom: "1px solid #E8E4DC", padding: "14px 24px", display: "flex", gap: "10px", alignItems: "flex-start" },
    disclaimerIcon: { fontSize: "13px", color: "#8A9890", marginTop: "1px", flexShrink: 0 },
    disclaimerText: { fontSize: "12px", color: "#6B7A72", lineHeight: "1.6" },
    overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: "24px" },
    modal: { background: "#F5F2EC", borderRadius: "10px", padding: "32px", maxWidth: "520px", width: "100%", maxHeight: "80vh", overflowY: "auto" },
    modalTitle: { fontFamily: "'Fraunces', Georgia, serif", fontSize: "22px", fontWeight: "600", marginBottom: "20px", color: "#1A1A1A" },
    modalSection: { marginBottom: "20px" },
    modalH3: { fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.8px", color: "#2A7A4F", fontWeight: "500", marginBottom: "6px" },
    modalP: { fontSize: "13px", color: "#5A6B62", lineHeight: "1.7" },
    modalClose: { background: "#1B3A2D", color: "#F5F2EC", border: "none", borderRadius: "5px", padding: "10px 24px", fontSize: "13px", cursor: "pointer", fontFamily: "inherit", fontWeight: "500", marginTop: "8px" },
  };

  return (
    <div style={c.root}>
      <nav style={c.nav}>
        <div style={{ display: "flex", alignItems: "center", gap: "2px" }}>
          <span style={c.navLogo}>myhst</span>
          <svg width="12" height="12" viewBox="0 0 100 100" fill="#C41E3A" style={{ margin: "0 1px", marginBottom: "8px" }}><path d="M50 5 L54 35 L70 20 L62 42 L85 38 L72 55 L90 65 L68 63 L72 85 L50 72 L28 85 L32 63 L10 65 L28 55 L15 38 L38 42 L30 20 L46 35 Z"/></svg>
          <span style={{ ...c.navLogo, color: "#7FB89A" }}>ca</span>
        </div>
        <span style={c.navTag}>Free for Canadian freelancers</span>
      </nav>

      <div style={c.hero}>
        <div style={c.heroEyebrow}>Canadian HST tracker</div>
        <h1 style={c.heroH1}>
          Know exactly what you <em style={c.heroH1Em}>owe CRA</em><br />before they ask
        </h1>
        <p style={c.heroSub}>
          Track every invoice, every ITC, and your remittance balance in real time. Free, no signup required.
        </p>
        <button style={c.heroCTA} onClick={() => { setShowTracker(true); setTimeout(() => document.getElementById('tracker')?.scrollIntoView({ behavior: 'smooth' }), 50); }}>
          Start tracking →
        </button>
        <div style={c.heroStatRow}>
          <div style={c.heroStat}>
            <div style={c.heroStatNum}>$30K</div>
            <div style={c.heroStatLabel}>registration threshold</div>
          </div>
          <div style={c.heroStat}>
            <div style={c.heroStatNum}>100%</div>
            <div style={c.heroStatLabel}>free to use</div>
          </div>
          <div style={c.heroStat}>
            <div style={c.heroStatNum}>7</div>
            <div style={c.heroStatLabel}>provinces supported</div>
          </div>
        </div>
      </div>

      <div style={c.pillars}>
        {[
          ["01", "Track your threshold", "See exactly how close you are to the $30,000 mandatory registration point. No more end-of-year surprises."],
          ["02", "Log every ITC", "Every HST dollar you pay on business expenses is money back in your pocket. We make sure you don't leave any on the table."],
          ["03", "Know your remittance", "Your net balance updates the moment you add an invoice or expense. Know your quarterly number before you open CRA MyAccount."],
        ].map(([num, title, body]) => (
          <div key={num} style={c.pillar}>
            <div style={c.pillarNum}>{num}</div>
            <div style={c.pillarTitle}>{title}</div>
            <p style={c.pillarBody}>{body}</p>
          </div>
        ))}
      </div>

      <div id="tracker" style={c.trackerWrap}>
        <div style={c.trackerHead}>
          <div style={{ flex: 1 }}>
            <div style={c.trackerTitle}>Your HST tracker</div>
            <div style={{ fontSize: "12px", color: "#7FB89A", marginTop: "4px" }}>Data lives in your browser — nothing is sent anywhere</div>
            <input
              style={{ marginTop: "10px", background: "rgba(255,255,255,0.08)", border: "1px solid #3A5E47", color: "#F5F2EC", padding: "6px 10px", fontSize: "13px", borderRadius: "4px", fontFamily: "inherit", width: "220px", outline: "none" }}
              placeholder="Your name or business name"
              value={businessName}
              onChange={e => setBusinessName(e.target.value)}
            />
          </div>
          <div style={{ display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap" }}>
            <button
              onClick={handleDownloadClick}
              style={{ background: "transparent", border: "1px solid #3A5E47", color: "#7FB89A", padding: "8px 14px", fontSize: "12px", borderRadius: "5px", cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", gap: "6px" }}>
              ↓ Download summary
            </button>
            <select style={c.provSelect} value={province} onChange={e => setProvince(e.target.value)}>
              {Object.keys(HST_RATES).map(p => <option key={p}>{p}</option>)}
            </select>
          </div>
        </div>

        <div style={c.summGrid}>
          {[
            ["Total revenue", fmt(totRevenue), "excl. HST", false],
            ["HST collected", fmt(totHST), "held in trust for CRA", false],
            ["Input tax credits", fmt(totITC), "HST you paid on expenses", false],
            ["Net owing", fmt(Math.abs(netOwing)), netOwing < 0 ? "refund coming" : "to remit to CRA", netOwing > 0],
          ].map(([label, val, sub, warn]) => (
            <div key={label} style={c.summCard(warn)}>
              <div style={c.summLabel}>{label}</div>
              <div style={c.summVal(warn)}>{val}</div>
              <div style={c.summSub}>{sub}</div>
            </div>
          ))}
        </div>

        <div style={c.threshWrap}>
          <div style={c.threshRow}>
            <span>Registration threshold — {fmt(totRevenue)} of {fmt(THRESHOLD)}</span>
            <span style={{ color: threshPct > 85 ? "#C94A2D" : "#8A9890" }}>{fmt(THRESHOLD - totRevenue)} remaining</span>
          </div>
          <div style={c.threshBar}><div style={c.threshFill(threshPct)} /></div>
          {threshPct > 85 && <div style={{ fontSize: "11px", color: "#C94A2D", marginTop: "5px" }}>You are approaching the mandatory HST registration threshold. Consider registering now.</div>}
        </div>

        <div style={c.tabRow}>
          {[["income", "Income"], ["expenses", "Expenses & ITCs"], ["quarterly", "Quarterly summary"]].map(([key, label]) => (
            <button key={key} style={c.tabBtn(tab === key)} onClick={() => setTab(key)}>{label}</button>
          ))}
        </div>

        {tab === "income" && (
          <>
            <table style={c.table}>
              <thead><tr>
                <th style={c.th}>Date</th><th style={c.th}>Client</th>
                <th style={{ ...c.th, textAlign: "right" }}>Pre-tax amount</th>
                <th style={{ ...c.th, textAlign: "right" }}>HST collected</th>
                <th style={c.th}></th>
              </tr></thead>
              <tbody>
                {income.map(r => (
                  <tr key={r.id}>
                    <td style={c.td}><input style={c.inlineInp} type="date" value={r.date} onChange={e => updateIncome(r.id, "date", e.target.value)} /></td>
                    <td style={c.td}><input style={{ ...c.inlineInp, width: "100%" }} placeholder="Client name" value={r.client} onChange={e => updateIncome(r.id, "client", e.target.value)} /></td>
                    <td style={c.td}><input style={{ ...c.inlineInp, textAlign: "right" }} type="number" placeholder="0.00" value={r.amount} onChange={e => updateIncome(r.id, "amount", e.target.value)} /></td>
                    <td style={c.tdG}><input style={{ ...c.inlineInp, textAlign: "right", color: "#2A7A4F" }} type="number" placeholder="0.00" value={r.hst} onChange={e => setIncome(p => p.map(x => x.id === r.id ? { ...x, hst: e.target.value } : x))} /></td>
                    <td style={c.td}><button style={c.delBtn} onClick={() => setIncome(p => p.filter(x => x.id !== r.id))}>×</button></td>
                  </tr>
                ))}
                {income.length === 0 && <tr><td colSpan={5} style={{ ...c.td, textAlign: "center", color: "#C0BCB4", padding: "24px" }}>No income entries yet — click New entry to start</td></tr>}
              </tbody>
            </table>
            <div style={c.addRow}>
              <button style={{ ...c.addBtn, opacity: income.length >= MAX_ENTRIES ? 0.4 : 1, cursor: income.length >= MAX_ENTRIES ? "not-allowed" : "pointer" }} onClick={addIncomeRow} disabled={income.length >= MAX_ENTRIES}>+ New entry</button>
              {income.length >= MAX_ENTRIES && <span style={{ fontSize: "12px", color: "#C94A2D", alignSelf: "center" }}>Maximum of {MAX_ENTRIES} entries reached</span>}
            </div>
          </>
        )}

        {tab === "expenses" && (
          <>
            <table style={c.table}>
              <thead><tr>
                <th style={c.th}>Date</th><th style={c.th}>Description</th>
                <th style={{ ...c.th, textAlign: "right" }}>Pre-tax amount</th>
                <th style={{ ...c.th, textAlign: "right" }}>ITC claimable</th>
                <th style={c.th}></th>
              </tr></thead>
              <tbody>
                {expenses.map(r => (
                  <tr key={r.id}>
                    <td style={c.td}><input style={c.inlineInp} type="date" value={r.date} onChange={e => updateExpense(r.id, "date", e.target.value)} /></td>
                    <td style={c.td}><input style={{ ...c.inlineInp, width: "100%" }} placeholder="Description" value={r.desc} onChange={e => updateExpense(r.id, "desc", e.target.value)} /></td>
                    <td style={c.td}><input style={{ ...c.inlineInp, textAlign: "right" }} type="number" placeholder="0.00" value={r.amount} onChange={e => updateExpense(r.id, "amount", e.target.value)} /></td>
                    <td style={c.tdG}><input style={{ ...c.inlineInp, textAlign: "right", color: "#2A7A4F" }} type="number" placeholder="0.00" value={r.itc} onChange={e => setExpenses(p => p.map(x => x.id === r.id ? { ...x, itc: e.target.value } : x))} /></td>
                    <td style={c.td}><button style={c.delBtn} onClick={() => setExpenses(p => p.filter(x => x.id !== r.id))}>×</button></td>
                  </tr>
                ))}
                {expenses.length === 0 && <tr><td colSpan={5} style={{ ...c.td, textAlign: "center", color: "#C0BCB4", padding: "24px" }}>No expenses yet — click New entry to start</td></tr>}
              </tbody>
            </table>
            <div style={c.addRow}>
              <button style={{ ...c.addBtn, opacity: expenses.length >= MAX_ENTRIES ? 0.4 : 1, cursor: expenses.length >= MAX_ENTRIES ? "not-allowed" : "pointer" }} onClick={addExpenseRow} disabled={expenses.length >= MAX_ENTRIES}>+ New entry</button>
              {expenses.length >= MAX_ENTRIES && <span style={{ fontSize: "12px", color: "#C94A2D", alignSelf: "center" }}>Maximum of {MAX_ENTRIES} entries reached</span>}
            </div>
          </>
        )}

        {tab === "quarterly" && (
          <table style={c.table}>
            <thead><tr>
              <th style={c.th}>Quarter</th>
              <th style={{ ...c.th, textAlign: "right" }}>HST collected</th>
              <th style={{ ...c.th, textAlign: "right" }}>ITCs</th>
              <th style={{ ...c.th, textAlign: "right" }}>Net remittance</th>
            </tr></thead>
            <tbody>
              {qData.map((q, i) => (
                <tr key={i}>
                  <td style={c.td}>{q.label}</td>
                  <td style={c.tdR}>{q.collected > 0 ? fmt(q.collected) : "—"}</td>
                  <td style={c.tdG}>{q.itcs > 0 ? "−" + fmt(q.itcs) : "—"}</td>
                  <td style={{ ...c.tdR, color: q.net > 0 ? "#8A5A00" : q.net < 0 ? "#2A7A4F" : "#C0BCB4", fontWeight: q.net !== 0 ? "500" : "400" }}>
                    {q.net !== 0 ? (q.net < 0 ? fmt(Math.abs(q.net)) + " refund" : fmt(q.net)) : "—"}
                  </td>
                </tr>
              ))}
              <tr style={{ background: "#F9F7F2" }}>
                <td style={{ ...c.td, fontWeight: "500" }}>Full year</td>
                <td style={{ ...c.tdR, fontWeight: "500" }}>{fmt(totHST)}</td>
                <td style={{ ...c.tdG, fontWeight: "500" }}>−{fmt(totITC)}</td>
                <td style={{ ...c.tdR, fontWeight: "500", color: netOwing >= 0 ? "#8A5A00" : "#2A7A4F" }}>
                  {netOwing >= 0 ? fmt(netOwing) : fmt(Math.abs(netOwing)) + " refund"}
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr><td colSpan={4} style={{ padding: "10px 16px", fontSize: "11px", color: "#AAB4AC", borderTop: "1px solid #F2EFE8" }}>
                Quarterly remittance due one month after quarter end. Annual filers: balance due April 30, return due June 15.
              </td></tr>
            </tfoot>
          </table>
        )}
      </div>

      <div style={c.disclaimer}>
        <span style={c.disclaimerIcon}>ⓘ</span>
        <span style={c.disclaimerText}>
          <strong style={{ color: "#3A3A3A" }}>Not tax advice.</strong> myhst.ca is a calculation tool for informational purposes only and does not constitute professional tax or legal advice. HST/GST rates are current as of 2025 and may not reflect recent legislative changes. Always verify your remittance amount with the CRA or a qualified tax professional before filing. The accuracy of results depends entirely on the information you enter.
        </span>
      </div>

      <div style={c.emailSection}>
        {emailSent ? (
          <div>
            <div style={c.successMsg}>You're on the list.</div>
            <p style={{ color: "#7FB89A", marginTop: "12px", fontSize: "14px" }}>We'll let you know when the full app — with saved data, remittance reminders, and year-end reports — is ready.</p>
          </div>
        ) : (
          <>
            <h2 style={c.emailH2}>The full app is coming</h2>
            <p style={c.emailSub}>Saved data, remittance reminders, year-end reports, and Quick Method support. Be first to know.</p>
            <div style={c.emailForm}>
              <input style={c.emailInp} type="email" placeholder="your@email.com" value={email} onChange={e => setEmail(e.target.value)} onKeyDown={e => e.key === "Enter" && handleEmail()} />
              <button style={c.emailBtn} onClick={handleEmail} disabled={sending}>{sending ? "Sending…" : "Notify me"}</button>
            </div>
            {emailError && <div style={c.emailErr}>{emailError}</div>}
            <p style={{ fontSize: "11px", color: "#4A6655", marginTop: "16px" }}>No spam. Unsubscribe anytime.</p>
          </>
        )}
      </div>

      <footer style={c.footer}>
        <span style={c.footerLogo}>myhst.ca</span>
        <span style={c.footerNote}>Built for Canadian freelancers · {new Date().getFullYear()} · Not tax advice · Results are estimates only</span>
        <button style={c.footerLink} onClick={() => setShowPrivacy(true)}>Privacy</button>
      </footer>

      {showPrivacy && (
        <div style={c.overlay} onClick={() => setShowPrivacy(false)}>
          <div style={c.modal} onClick={e => e.stopPropagation()}>
            <div style={c.modalTitle}>Privacy &amp; legal</div>

            <div style={c.modalSection}>
              <div style={c.modalH3}>Not tax advice</div>
              <p style={c.modalP}>myhst.ca is a calculation tool provided for informational purposes only. It does not constitute tax, legal, or financial advice. Results are estimates based on the information you provide. Always consult a CPA or qualified tax professional before filing with the CRA.</p>
            </div>

            <div style={c.modalSection}>
              <div style={c.modalH3}>Accuracy</div>
              <p style={c.modalP}>HST/GST rates displayed are current as of 2025. Tax legislation changes periodically — we cannot guarantee rates are current at the time you use this tool. We accept no liability for errors, omissions, or outcomes arising from use of this calculator.</p>
            </div>

            <div style={c.modalSection}>
              <div style={c.modalH3}>Your data</div>
              <p style={c.modalP}>All income and expense entries you make in the tracker live only in your browser. We do not store, transmit, or have access to any financial data you enter. Closing or refreshing the page will clear your entries.</p>
            </div>

            <div style={c.modalSection}>
              <div style={c.modalH3}>Email collection</div>
              <p style={c.modalP}>If you choose to leave your email address, it is collected solely to notify you about myhst.ca product updates. Your email is processed by Formspree (formspree.io), a third-party form service. We do not sell, rent, or share your email with any other third parties. You can unsubscribe at any time by replying to any email we send. This practice is consistent with Canada's federal privacy law (PIPEDA).</p>
            </div>

            <div style={c.modalSection}>
              <div style={c.modalH3}>Contact</div>
              <p style={c.modalP}>Questions about this policy? Email us at hello@myhst.ca</p>
            </div>

            <button style={c.modalClose} onClick={() => setShowPrivacy(false)}>Close</button>
          </div>
        </div>
      )}

      {showDownloadModal && (
        <div style={c.overlay} onClick={() => setShowDownloadModal(false)}>
          <div style={{ ...c.modal, maxWidth: "400px" }} onClick={e => e.stopPropagation()}>
            <div style={c.modalTitle}>One last thing</div>
            <p style={{ ...c.modalP, marginBottom: "20px" }}>Enter your email to download your summary. We'll also notify you when the full app — with saved data and remittance reminders — is ready.</p>
            <input
              style={{ width: "100%", background: "#fff", border: "1px solid #D8D4CB", borderRadius: "5px", padding: "10px 12px", fontSize: "14px", fontFamily: "inherit", color: "#1A1A1A", outline: "none", boxSizing: "border-box", marginBottom: "10px" }}
              type="email"
              placeholder="your@email.com"
              value={downloadEmail}
              onChange={e => setDownloadEmail(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleDownloadWithEmail()}
              autoFocus
            />
            {downloadEmailError && <div style={{ fontSize: "12px", color: "#C94A2D", marginBottom: "8px" }}>{downloadEmailError}</div>}
            <div style={{ display: "flex", gap: "10px" }}>
              <button style={c.modalClose} onClick={handleDownloadWithEmail}>Download</button>
              <button style={{ ...c.modalClose, background: "transparent", color: "#8A9890", border: "1px solid #D8D4CB" }} onClick={() => setShowDownloadModal(false)}>Cancel</button>
            </div>
            <p style={{ fontSize: "11px", color: "#AAB4AC", marginTop: "12px" }}>No spam. Unsubscribe anytime. See our <button style={{ background: "none", border: "none", color: "#2A7A4F", cursor: "pointer", fontSize: "11px", padding: 0, fontFamily: "inherit" }} onClick={() => { setShowDownloadModal(false); setShowPrivacy(true); }}>privacy policy</button>.</p>
          </div>
        </div>
      )}
    </div>
  );
}
